import { Alert, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { UploadModule } from '@/api/files';
import { storageKey, type StorageAdmin, type UsageGroup, type UsageRow } from '@/api/storage-admin';
import { formatBytes, FormField, type FieldDef } from '@/components/fields';
import { LocalDataTable } from '@/components/grid/LocalDataTable';
import { MODULE_LABELS, serverError } from './common';
import { ROLE_OPTIONS } from './configFields';

const GROUP_FIELD: FieldDef = {
  type: 'radio', name: 'group', label: 'Show usage by', options: [
    { value: 'user', label: 'Person' },
    { value: 'module', label: 'Where it was uploaded' },
    { value: 'role', label: 'Role' },
  ],
};

const SIZE: Partial<GridColDef<UsageRow>> = { type: 'number', minWidth: 120, valueFormatter: (v: number) => formatBytes(v ?? 0) };

const FIRST_COLUMN: Record<UsageGroup, GridColDef<UsageRow>> = {
  user: { field: 'name', headerName: 'Person', minWidth: 240, flex: 1,
          valueGetter: (_v, r) => (r.name ? `${r.name}${r.email ? ` · ${r.email}` : ''}` : r.user_id) },
  module: { field: 'module', headerName: 'Where it was uploaded', minWidth: 240, flex: 1,
            valueGetter: (_v, r) => MODULE_LABELS[r.module as UploadModule] ?? r.module },
  role: { field: 'role', headerName: 'Role', minWidth: 200, flex: 1,
          valueGetter: (_v, r) => ROLE_OPTIONS.find((o) => o.value === r.role)?.label ?? r.role },
};

/** Who and what is using the space. */
export function UsageSection({ admin }: { admin: StorageAdmin }) {
  const [group, setGroup] = useState<UsageGroup>('user');
  const usage = useQuery({ queryKey: storageKey(admin, 'usage', group), queryFn: () => admin.usage!(group) });
  const columns: GridColDef<UsageRow>[] = [
    FIRST_COLUMN[group],
    { ...SIZE, field: 'used_bytes', headerName: 'Stored' },
    ...(group === 'user' ? [{ ...SIZE, field: 'reserved_bytes', headerName: 'Uploading now' }] : []),
    { field: 'file_count', headerName: 'Files', type: 'number', minWidth: 90 },
  ];
  return (
    <Stack spacing={2}>
      <FormField field={GROUP_FIELD} value={group} onChange={(v) => setGroup(v as UsageGroup)} />
      {usage.error && <Alert severity="error">{serverError(usage.error).message}</Alert>}
      <LocalDataTable
        rows={usage.data ?? []} columns={columns} loading={usage.isLoading}
        getRowId={(r) => r.user_id ?? r.module ?? r.role ?? 'unknown'}
        searchText={(r) => `${r.name ?? ''} ${r.email ?? ''} ${r.module ?? ''} ${r.role ?? ''}`}
        searchPlaceholder="Search" noun={['row', 'rows']}
        summary={(rows) => [
          ['Stored', formatBytes(rows.reduce((n, r) => n + r.used_bytes, 0))],
          ['Files', rows.reduce((n, r) => n + r.file_count, 0).toLocaleString('en-IN')],
        ]}
        emptyMessage="Nothing stored yet."
      />
    </Stack>
  );
}
